import ImmutableModel, {
  asModel,
  asInstance,
  asNullOr,
  asString,
  asBoolean,
  withDefault
} from '@aytm/lib/immutable/ImmutableModel'
import ItemsOrderedMap from '@aytm/lib/immutable/ItemsOrderedMap'

import { selectFilters } from '@aytm/app/insights/dashboard/lib/filters'

import { generateUID } from '@aytm/app/insights/dashboard/lib/strings'

import BlockModel, { FiltersOrderedMap, GroupListModel } from './BlockModel'
import getBlockModelByType from './blocks/index'

export class TimeZoneModel extends ImmutableModel({
  title: asString,
  offset: asString
}) {}
ImmutableModel.decorator({ name: 'TimeZoneModel' })(TimeZoneModel)

export const BlocksOrderedMap = ItemsOrderedMap(
  x => (BlockModel.isBlockModel(x) ? x : getBlockModelByType(x?.type).of(x)),
  null,
  'BlocksOrderedMap'
)

export const getEmptyDashboardSchemaModel = () => DashboardSchemaModel.empty()

export const getDashboardSchemaModelFrom = input => DashboardSchemaModel.of(input)

class DashboardSchemaModel extends ImmutableModel({
  version: asNullOr(asString),
  timeZone: asNullOr(asModel(TimeZoneModel)),
  currentBlockUID: asNullOr(asString),
  activeTabID: asNullOr(withDefault('portal', asString)),
  showSidebar: asNullOr(withDefault(true, asBoolean)),
  createdAt: asNullOr(asString),
  createdBy: asNullOr(asString),
  updatedAt: asNullOr(asString),
  updatedBy: asNullOr(asString),
  patchedAt: asNullOr(asString),
  filters: asNullOr(asInstance(FiltersOrderedMap.isFiltersOrderedMap, FiltersOrderedMap)),
  groups: asNullOr(asInstance(GroupListModel.isGroupListModel, GroupListModel)),
  blocks: asNullOr(asInstance(BlocksOrderedMap.isBlocksOrderedMap, BlocksOrderedMap))
}) {
  static empty() {
    return DashboardSchemaModel.of({
      blocks: [],
      currentBlockUID: null
    })
  }
  static create(type) {
    return getBlockModelByType(type).empty()
  }
  copyBlock(uid, patch) {
    const source = this.getIn(['blocks', uid])
    if (source) {
      const type = source.get('type')
      return getBlockModelByType(type)?.of({
        ...source.toJS(),
        uid: generateUID(),
        purpose: 'view',
        createdAt: null,
        createdBy: null,
        updatedAt: null,
        updatedBy: null,
        ...patch
      })
    }
    return null
  }
  getFilters(mask) {
    return selectFilters(this.get('filters'), mask)
  }
  getCurrentBlock() {
    const currentBlockUID = this.get('currentBlockUID')
    if (currentBlockUID) {
      return this.getIn(['blocks', currentBlockUID])
    }
    return null
  }
  addBlock(block, uid, position = 1) {
    if (!block) return this
    const blockUID = block.get('uid')
    let newBlocks = this.get('blocks') || BlocksOrderedMap()
    if (newBlocks.has(uid)) {
      // Insert block before/after block with provided 'uid'
      // depending on 'position' property
      newBlocks = newBlocks.reduce((acc, cur) => {
        let result = acc
        const curUid = cur.get('uid')
        if (uid === curUid) {
          if (0 < position) {
            // Inserts new block after given block
            result = result.set(curUid, cur)
            result = result.set(blockUID, block)
          } else if (0 > position) {
            // Inserts new block before given block
            result = result.set(curUid, cur)
            result = result.set(blockUID, block)
          } else {
            // Replace block with given 'uid' with new one
            result = result.set(curUid, block)
          }
        } else {
          result = result.set(curUid, cur)
        }
        return result
      }, BlocksOrderedMap())
    } else {
      // If the block with given 'uid' is not found
      newBlocks =
        0 <= position
          ? // Add to the bottom
            newBlocks.set(blockUID, block)
          : // Add to the top
            newBlocks.reduce((acc, cur) => acc.set(cur.get('uid'), cur), BlocksOrderedMap().set(blockUID, block))
    }
    return this.set('blocks', newBlocks)
  }
}
ImmutableModel.decorator({ name: 'DashboardSchemaModel' })(DashboardSchemaModel)
export default DashboardSchemaModel
